import React, { useMemo, useCallback } from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { SimpleTableMobileDetailModal } from "../../UniversalCRMTable/SimpleTableMobileDetailModal";
import { UniversalDetailActions } from "../../UniversalDetailPage/UniversalDetailActions";
import { EntityDetailModalLayout } from "../../UniversalDetailModalLayout";
import { DetailHeaderContent } from "../../UniversalDetailPage/components/DetailHeaderContent";
import { DetailActionProvider } from "@/contexts/DetailActionContext";
import { DetailContextProvider } from "@/contexts/DetailContextProvider";
import { DetailModalHeaderActions } from "./DetailModalHeaderActions";
import { DetailModalLoadingState, DetailModalErrorState } from "./DetailModalStatusStates";
import { DetailModalNavigationButtons } from "./DetailModalNavigationButtons";
import { buildDetailHeaderEntityData } from "../../UniversalDetailPage/utils/build-detail-header-entity-data";
import {
  areDetailModalContentPropsEqual,
  type DetailModalContentProps,
} from "./detailModalContentEquality";
import type { BaseEntity } from "../../UniversalCRMTable/types/shared";

export function DetailModalContentComponent<T extends BaseEntity>({
  itemId,
  item,
  isLoading,
  error,
  refetch,
  modalId,
  entityName,
  config,
  actionContext,
  detailActions,
  entityTabsConfig,
  activeTab,
  setActiveTab,
  headerTitle,
  headerSubtitle,
  canNavigate,
  canGoPrevious,
  canGoNext,
  handlePrevious,
  handleNext,
  handleInlineEdit,
  handleEdit,
  handleDelete,
  parentEntity,
  linkedCompanyAccount = null,
}: DetailModalContentProps<T>) {
  // Don't render if no item ID - MUST be before any hooks
  if (!itemId) {
    return null;
  }

  // Memoize callbacks to prevent child re-renders
  const memoizedSetActiveTab = useCallback((tab: string) => setActiveTab(tab), [setActiveTab]);
  const memoizedHandleInlineEdit = useCallback(
    (updatedItem: T) => handleInlineEdit(updatedItem),
    [handleInlineEdit]
  );
  const memoizedHandleEdit = useCallback((updatedItem: T) => handleEdit(updatedItem), [handleEdit]);
  const memoizedHandleDelete = useCallback(
    (updatedItem: T) => handleDelete(updatedItem),
    [handleDelete]
  );
  const memoizedHandleTagsUpdate = useCallback(
    async (newTags: string[]) => {
      if (!item) return;
      await memoizedHandleInlineEdit({
        ...item,
        tags: newTags,
      });
      // refetch() is not needed: mutation refetchQueries handles single + list queries.
    },
    [item, memoizedHandleInlineEdit]
  );

  const entityData = useMemo(() => {
    if (!item) return null;
    return buildDetailHeaderEntityData(item, config.entity);
  }, [item, config.entity]);

  // Show loading state ONLY if we don't have an item yet
  // If we have an item, show it even if isLoading is true (e.g., during background refetches)
  if (isLoading && !item) {
    return <DetailModalLoadingState modalId={modalId} displayName={config.displayName} />;
  }

  if (error) {
    return (
      <DetailModalErrorState
        modalId={modalId}
        displayName={config.displayName}
        error={error}
        refetch={refetch}
      />
    );
  }

  // If we have entity-specific tabs config, use the new layout
  if (entityTabsConfig && actionContext) {
    const navigationButtons =
      canNavigate === true ? (
        <DetailModalNavigationButtons
          orientation="horizontal"
          tooltipPosition="top"
          canGoPrevious={canGoPrevious === true}
          canGoNext={canGoNext === true}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      ) : undefined;

    return (
      <DetailContextProvider parentEntity={parentEntity} linkedCompanyAccount={linkedCompanyAccount}>
        <DetailActionProvider value={actionContext}>
          <EntityDetailModalLayout
            item={item as any}
            entityName={entityName}
            config={config}
            headerTitle={headerTitle}
            headerSubtitle={headerSubtitle}
            actions={detailActions}
            tabs={entityTabsConfig.tabs}
            activeTab={activeTab}
            onTabChange={memoizedSetActiveTab}
            renderPrimaryArea={entityTabsConfig.renderPrimaryArea}
            renderInfoPanel={entityTabsConfig.renderInfoPanel}
            renderInlineInfoPanel={entityTabsConfig.renderInlineInfoPanel}
            onInlineEdit={memoizedHandleInlineEdit}
            onTagsUpdate={memoizedHandleTagsUpdate}
            globalModalId={modalId}
            navigationButtons={navigationButtons}
          />
        </DetailActionProvider>
      </DetailContextProvider>
    );
  }

  // Fallback to old layout for entities without tabs config
  if (actionContext) {
    const navigationButtons =
      canNavigate === true ? (
        <DetailModalNavigationButtons
          orientation="vertical"
          tooltipPosition="right"
          canGoPrevious={canGoPrevious === true}
          canGoNext={canGoNext === true}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      ) : undefined;

    const headerContent =
      item && entityData ? (
        <DetailHeaderContent
          item={item}
          config={config}
          pageTitle={headerTitle}
          pageDescription={headerSubtitle}
          entityData={entityData}
          onTagsUpdate={memoizedHandleTagsUpdate}
          onInlineEdit={memoizedHandleInlineEdit}
        />
      ) : null;

    return (
      <DetailContextProvider parentEntity={parentEntity} linkedCompanyAccount={linkedCompanyAccount}>
        <DetailActionProvider value={actionContext}>
          <ResponsiveModal
            globalModalId={modalId}
            title={headerContent || headerTitle}
            maxWidth="90vw"
            side="bottom"
            headerContent={<DetailModalHeaderActions />}
          >
            {(navigationButtons || detailActions.length > 0) && (
              <div className="flex items-center justify-between gap-2 mb-4 -mx-6 px-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                {navigationButtons}
                {detailActions.length > 0 ? (
                  <UniversalDetailActions actions={detailActions} maxActions={3} />
                ) : undefined}
              </div>
            )}

            <SimpleTableMobileDetailModal
              item={item as any}
              config={config}
              onEdit={memoizedHandleEdit}
              onDelete={memoizedHandleDelete}
              onInlineEdit={memoizedHandleInlineEdit}
            />
          </ResponsiveModal>
        </DetailActionProvider>
      </DetailContextProvider>
    );
  }

  return null;
}

export const DetailModalContent = React.memo(
  DetailModalContentComponent,
  areDetailModalContentPropsEqual
) as typeof DetailModalContentComponent;
